/*
 * Copyright The Adaptive Palette copyright holders
 * See the AUTHORS.md file at the top-level directory of this distribution and at
 * https://github.com/inclusive-design/adaptive-palette/raw/main/AUTHORS.md.
 *
 * Licensed under the New BSD license. You may not use this file except in
 * compliance with this License.
 *
 * You may obtain a copy of the License at
 * https://github.com/inclusive-design/adaptive-palette/blob/main/LICENSE
 */

import { VNode } from "preact";
import { html } from "htm/preact";

import { changeEncodingContents, finishedMessageSignal } from "../../state/GlobalData";
import { saveMessageRecord } from "../../core/MessageLog";
import {
  canReopenSentences, currentTelegraphicMessage, makeSentences, reopenSentences,
  sentenceCompletionsSignal
} from "./TelegraphicTranslationState";
import { BlissSymbolInfoType, LayoutInfoType } from "../../index.d";
import { BlissSymbol } from "../../components/BlissSymbol";
import { generateGridStyle } from "../../utils/GridUtils";
import { normalizeComposition } from "../../utils/SymbolEncodingUtils";
import { speakUnavailable } from "../../utils/SpeechUtils";

type CommandMakeSentenceProps = {
  id: string,
  options: LayoutInfoType & Partial<BlissSymbolInfoType> & {
    label: string,
    ariaControls?: string
  }
};

/**
 * This button is the entry point to trigger telegraphic translation. The palette leaves it out
 * when the feature is unavailable: its cell is flagged `requiresModel` and `requiresConfig` in
 * `input_area.json`.
 * @param {CommandMakeSentenceProps} props - The cell id and its palette options.
 * @returns {VNode}
 */
export function CommandMakeSentence (props: CommandMakeSentenceProps): VNode {
  const { id, options } = props;
  const { label, composition, columnStart, columnSpan, rowStart, rowSpan, ariaControls } = options;
  const isFetching = sentenceCompletionsSignal.value.status === "working";
  const telegraphicMessage = currentTelegraphicMessage();

  const gridStyles = generateGridStyle(columnStart, columnSpan, rowStart, rowSpan);

  // Marked unavailable rather than `disabled`: a disabled button loses focus when
  // it is disabled, which costs a switch or eye-gaze user loses their scan position in
  // the middle of the interaction. `aria-disabled` says the same thing to assistive
  // technology while keeping the element focusable.
  // Kept sentences for this message, hidden by Home or Back or left on another palette, open
  // again. This works even while they are still being made.
  const canReopen = canReopenSentences(telegraphicMessage);
  const cannotRun = (isFetching && !canReopen) || telegraphicMessage.trim().length === 0;

  return html`
    <button
      id="${id}"
      class="btn-makeSentence btn-command"
      style="${gridStyles}"
      aria-controls=${ariaControls}
      aria-disabled=${cannotRun}
      onClick=${() => {
    if (cannotRun) {
      speakUnavailable(label);
      return;
    }
    // The message is finished, so the row stops reporting on it. Its words stay usable.
    finishedMessageSignal.value = telegraphicMessage;
    // Already saved and already made: show them again without asking the model.
    if (canReopen) {
      reopenSentences();
      return;
    }
    // Asking for a sentence means the message is finished, so save it.
    saveMessageRecord(changeEncodingContents.value.payloads);
    void makeSentences(telegraphicMessage);
  }}>
      ${composition
    ? html`<${BlissSymbol}
          composition=${normalizeComposition(composition)}
          label=${label}
          isPresentation=true
        />`
    : label}
    </button>
  `;
}
